import { useState } from 'react';
import type { PolicyAcceptanceStatus, PolicyKind } from '@accounting/shared';
import { useAcceptPolicy, usePolicyAcceptanceStatus } from '../../hooks/usePolicyAcceptance';
import { useAuth } from '../../auth/useAuth';

const POLICY_LABEL: Record<PolicyKind, { title: string; href: string }> = {
  privacy_policy: { title: 'Privacy Policy', href: '/privacy' },
  terms_of_service: { title: 'Terms of Service', href: '/terms-of-service' },
};

/**
 * Wraps every authenticated screen. Fetches the user's policy-acceptance status;
 * if any required policy is outstanding (not accepted at the CURRENT version),
 * renders a blocking acceptance screen instead of the children.
 */
export function ConsentGate({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = usePolicyAcceptanceStatus();

  // Don't render an empty page during the first round-trip — but don't block
  // forever either: if the call fails, surface a clear error rather than a
  // permanent gate-or-spinner state.
  if (isLoading) return <SmallCenter>Loading…</SmallCenter>;
  if (error) {
    return (
      <SmallCenter>
        <p className="field-error">Could not check policy acceptance. Refresh to try again.</p>
      </SmallCenter>
    );
  }
  if (!data || data.outstanding.length === 0) return <>{children}</>;
  return <AcceptanceScreen status={data} />;
}

function AcceptanceScreen({ status }: { status: PolicyAcceptanceStatus }) {
  const { logout } = useAuth();
  const accept = useAcceptPolicy();
  const [checks, setChecks] = useState<Record<string, boolean>>({});

  const toggle = (k: string) => setChecks((p) => ({ ...p, [k]: !p[k] }));

  const allChecked = status.outstanding.every(
    (o) => checks[`${o.policyKind}::${o.policyVersion}`],
  );

  const handleAccept = async () => {
    // Record each outstanding acceptance in sequence. The hook invalidates the
    // status query on success; once all are accepted, the gate falls through to
    // the children.
    for (const o of status.outstanding) {
      await accept.mutateAsync({ policyKind: o.policyKind, policyVersion: o.policyVersion });
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, background: 'var(--bg-page)' }}>
      <div
        className="card"
        style={{ maxWidth: 520, width: '100%', padding: 32, background: 'var(--bg-surface)', color: 'var(--text-default)' }}
      >
        <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 8, color: 'var(--text-strong)' }}>
          Before you continue
        </h1>
        <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--text-muted)', fontSize: 14 }}>
          {status.outstanding.length === 1
            ? 'Please review and accept the policy below to continue using the application.'
            : 'Please review and accept the policies below to continue using the application.'}
        </p>

        <div style={{ marginBottom: 20 }}>
          {status.outstanding.map((o) => {
            const meta = POLICY_LABEL[o.policyKind] ?? {
              title: o.policyKind,
              href: '#',
            };
            const key = `${o.policyKind}::${o.policyVersion}`;
            return (
              <label
                key={key}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, fontSize: 14, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={!!checks[key]}
                  onChange={() => toggle(key)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I have read and agree to the{' '}
                  <a
                    href={meta.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{ color: 'var(--accent, #2563eb)' }}
                  >
                    {meta.title}
                  </a>{' '}
                  <span className="muted" style={{ fontSize: 12 }}>(v{o.policyVersion})</span>.
                </span>
              </label>
            );
          })}
        </div>

        {accept.error && (
          <div className="field-error" style={{ marginBottom: 12, fontSize: 13 }}>
            {accept.error instanceof Error ? accept.error.message : 'Could not record acceptance.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={() => void logout()}>
            Sign out
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!allChecked || accept.isPending}
            onClick={() => void handleAccept()}
          >
            {accept.isPending ? 'Saving…' : 'Continue'}
          </button>
        </div>

        <p className="muted" style={{ fontSize: 11, marginTop: 20, marginBottom: 0 }}>
          Your acceptance is recorded with a timestamp and your account identifier as part of the audit log.
        </p>
      </div>
    </div>
  );
}

function SmallCenter({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '40vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div>{children}</div>
    </div>
  );
}
